import { useEffect, useState } from "react";
import httpClient from "services/network/httpClient";
import GenericModal from "components/Modal";
import CustomButton from "components/button/CustomButton";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type HelpDetails = {
    mobileVideoUrl?: string;
    mobileDocumentUrl?: string;
    webVideoUrl?: string;
    webDocumentUrl?: string;
};

const HelpPage = () => {
    const [helpDetails, setHelpDetails] = useState<HelpDetails>();
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewType, setPreviewType] = useState<"video" | "pdf" | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    // Track which download is in progress (by URL)
    const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);

    // ✅ Fetch Data
    const getDetails = async () => {
        try {
            const response: any = await httpClient.get(`/mobile-document/content`);
            setHelpDetails(response.value);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        getDetails();
    }, []);

    // ✅ View Handler
    const handleView = (url: string, type: "video" | "pdf") => {
        setIsLoading(true);
        setPreviewUrl(url);
        setPreviewType(type);
    };

    // ✅ S3-safe Download
    const handleDownload = async (url: string) => {
        setDownloadingUrl(url);
        try {
            const response = await fetch(url);
            const blob = await response.blob();

            // ✅ Extract clean filename (remove query params)
            const cleanUrl = url.split("?")[0];
            let fileName = cleanUrl.split("/").pop() || "file";

            // ✅ Ensure correct extension
            if (blob.type.includes("video") && !fileName.endsWith(".mp4")) {
                fileName += ".mp4";
            }
            if (blob.type.includes("pdf") && !fileName.endsWith(".pdf")) {
                fileName += ".pdf";
            }

            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            link.click();

            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed", err);
        } finally {
            setDownloadingUrl(null);
        }
    };

    const closeModal = () => {
        setPreviewUrl(null);
        setPreviewType(null);
        setIsLoading(false);
    };

    // ✅ Dynamic items (no duplication)
    const items = [
        {
            label: "📱 Mobile App Video",
            desc: "Watch tutorial video",
            url: helpDetails?.mobileVideoUrl,
            type: "video",
        },
        {
            label: "🌐 Web App Video",
            desc: "Watch tutorial video",
            url: helpDetails?.webVideoUrl,
            type: "video",
        },
        {
            label: "📄 Mobile Documentation",
            desc: "Read PDF guide",
            url: helpDetails?.mobileDocumentUrl,
            type: "pdf",
        },
        {
            label: "📄 Web Documentation",
            desc: "Read PDF guide",
            url: helpDetails?.webDocumentUrl,
            type: "pdf",
        },
    ];

    return (
        <div className="h-full bg-gray-100 p-6 dark:bg-table-bgDark">
            <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-2xl p-6 dark:bg-table-headerDark">

                <h1 className="text-2xl font-semibold text-gray-800 mb-6 dark:text-table-textDark ">
                    Help & Documentation
                </h1>

                <div className="space-y-4 dark:text-table-textDark ">

                    {/* ✅ Dynamic Render */}
                    {items.filter(item => item.url).length > 0 ? (
                        items
                            .filter(item => item.url)
                            .map((item, index) => (
                                <div
                                    key={index}
                                    className="border p-4 rounded-lg flex justify-between items-center hover:shadow-md transition dark:text-table-textDark "
                                >
                                    <div className="dark:text-table-textDark ">
                                        <p className="font-medium text-gray-700 dark:text-table-textDark ">{item.label}</p>
                                        <p className="text-sm text-gray-500 dark:text-table-textDark  ">{item.desc}</p>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleView(item.url!, item.type as any)}
                                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >
                                            View
                                        </button>

                                        {/* <button
                                            onClick={() => handleDownload(item.url!)}
                                            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 dark:text-table-hoverDark"
                                        >
                                            Download
                                        </button> */}
                                        <CustomButton
                                            onClick={() => handleDownload(item.url!)}
                                            loading={downloadingUrl === item.url}
                                        >
                                            {downloadingUrl !== item.url && <FontAwesomeIcon icon={faDownload} className="mr-1.5" />} Download
                                        </CustomButton>
                                    </div>
                                </div>
                            ))
                    ) : (
                        <p className="text-center text-gray-500">
                            No help content available
                        </p>
                    )}
                </div>
            </div>

            {/* ✅ Preview Modal */}
            {previewUrl && (
                <GenericModal
                    isOpen={!!previewUrl}
                    onClose={closeModal}
                    header="Preview"
                    customWidth="md:w-[800px]"
                >
                    <div className="w-full h-[500px] flex items-center justify-center">

                        {/* Loading */}
                        {/* {isLoading && (
                            <p className="text-gray-500">Loading preview...</p>
                        )} */}

                        {/* VIDEO */}
                        {previewType === "video" && (
                            <video
                                controls
                                className="w-full h-full rounded"
                                onLoadedData={() => setIsLoading(false)}
                            >
                                <source src={previewUrl} type="video/mp4" />
                                Your browser does not support video playback.
                            </video>
                        )}

                        {/* PDF */}
                        {previewType === "pdf" && (
                            <iframe
                                src={`${previewUrl}#toolbar=0`}
                                title="Document Preview"
                                className="w-full h-full rounded"
                                onLoad={() => setIsLoading(false)}
                            />
                        )}

                    </div>

                    {/* Fallback open in new tab */}
                    <div className="text-right mt-2">
                        <a
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 text-sm underline"
                        >
                            Open in new tab
                        </a>
                    </div>
                </GenericModal>
            )}
        </div>
    );
};

export default HelpPage;